import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RestaurantPurchaseOrderDTO } from '../../../types/restaurant/RestaurantPurchaseOrderTypes';
import type {
  ProductionBatch,
  ProductionExecutionResult,
  ProductionPlanningSource,
  ProductionSuggestion,
} from '../../../types/restaurant/RestaurantProductionPlannerTypes';
import { PageMetadata } from '../../../types/restaurant/PagedResponse';

interface State {
  source: ProductionPlanningSource;
  purchaseId: number | '';
  purchases: RestaurantPurchaseOrderDTO[];
  suggestions: ProductionSuggestion[];
  suggestionCache: Record<number, ProductionSuggestion>;
  suggestionPage: PageMetadata | null;
  quantities: Record<number, string>;
  notes: string;
  history: ProductionBatch[];
  historyPage: PageMetadata | null;
  selectedBatch: ProductionBatch | null;
  result: ProductionExecutionResult | null;
  loading: boolean;
  loadingOptions: boolean;
  producing: boolean;
  historyLoading: boolean;
  calculated: boolean;
  batchModalOpen: boolean;
}

const initialState: State = {
  source: 'INVENTORY',
  purchaseId: '',
  purchases: [],
  suggestions: [],
  suggestionCache: {},
  suggestionPage: null,
  quantities: {},
  notes: '',
  history: [],
  historyPage: null,
  selectedBatch: null,
  result: null,
  loading: false,
  loadingOptions: false,
  producing: false,
  historyLoading: false,
  calculated: false,
  batchModalOpen: false,
};

const slice = createSlice({
  name: 'restaurantProductionPlanner',
  initialState,
  reducers: {
    resetProductionPlanner: (state) => {
      Object.assign(state, initialState);
    },
    setProductionSource: (
      state,
      action: PayloadAction<ProductionPlanningSource>
    ) => {
      state.source = action.payload;
      state.purchaseId = '';
      state.suggestions = [];
      state.suggestionCache = {};
      state.quantities = {};
      state.calculated = false;
      state.result = null;
    },
    setProductionPurchase: (state, action: PayloadAction<number | ''>) => {
      state.purchaseId = action.payload;
      state.suggestions = [];
      state.suggestionCache = {};
      state.quantities = {};
      state.calculated = false;
      state.result = null;
    },
    setProductionPurchases: (
      state,
      action: PayloadAction<RestaurantPurchaseOrderDTO[]>
    ) => {
      state.purchases = action.payload;
    },
    setProductionSuggestions: (
      state,
      action: PayloadAction<{ content: ProductionSuggestion[]; page: PageMetadata }>
    ) => {
      state.suggestions = action.payload.content;
      action.payload.content.forEach((suggestion) => {
        state.suggestionCache[suggestion.recipeId] = suggestion;
      });
      state.suggestionPage = action.payload.page;
      state.calculated = true;
      state.result = null;
    },
    setProductionQuantity: (
      state,
      action: PayloadAction<{ recipeId: number; quantity: string }>
    ) => {
      state.quantities[action.payload.recipeId] = action.payload.quantity;
    },
    setProductionNotes: (state, action: PayloadAction<string>) => {
      state.notes = action.payload;
    },
    setProductionHistory: (state, action: PayloadAction<{ content: ProductionBatch[]; page: PageMetadata }>) => {
      state.history = action.payload.content;
      state.historyPage = action.payload.page;
    },
    setProductionResult: (
      state,
      action: PayloadAction<ProductionExecutionResult | null>
    ) => {
      state.result = action.payload;
    },
    openProductionBatch: (state, action: PayloadAction<ProductionBatch>) => {
      state.selectedBatch = action.payload;
      state.batchModalOpen = true;
    },
    closeProductionBatch: (state) => {
      state.selectedBatch = null;
      state.batchModalOpen = false;
    },
    setProductionPlannerLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setProductionOptionsLoading: (state, action: PayloadAction<boolean>) => {
      state.loadingOptions = action.payload;
    },
    setProductionProducing: (state, action: PayloadAction<boolean>) => {
      state.producing = action.payload;
    },
    setProductionHistoryLoading: (state, action: PayloadAction<boolean>) => {
      state.historyLoading = action.payload;
    },
  },
});

export const {
  resetProductionPlanner,
  setProductionSource,
  setProductionPurchase,
  setProductionPurchases,
  setProductionSuggestions,
  setProductionQuantity,
  setProductionNotes,
  setProductionHistory,
  setProductionResult,
  openProductionBatch,
  closeProductionBatch,
  setProductionPlannerLoading,
  setProductionOptionsLoading,
  setProductionProducing,
  setProductionHistoryLoading,
} = slice.actions;
export default slice.reducer;
